import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import * as _ from "lodash";
import { of } from "rxjs";
import { catchError, map, switchMap, withLatestFrom } from "rxjs/operators";

import { UserPreferencesService } from "../../../services/api/user-prefs.service";
import { LoginProcessed, SessionRefreshed } from "../../actions/user.actions";
import { UserPreferencesActions } from "../../actions/user-prefs.actions";
import { FailState } from "../../models/fail-state";
import { UserPreferencesState } from "../../models/user-prefs/user-prefs-state";
import { AppState } from "../../reducers";
import { UserPreferencesSelectors } from "../../selectors/user-prefs.selectors";

@Injectable()
export class UserPreferencesGetEffects {
	get = createEffect(() =>
		this.actions.pipe(
			ofType(UserPreferencesActions.prefs.get),
			withLatestFrom(this.store.select(UserPreferencesSelectors.userPreferences)),
			switchMap(([action, state]) =>
				this.canUseStoreValue(state)
					? of(UserPreferencesActions.prefs.getSuccess(state))
					: of(UserPreferencesActions.prefs.getApi())
			)
		)
	);

	getApi = createEffect(() =>
		this.actions.pipe(
			ofType(UserPreferencesActions.prefs.getApi),
			switchMap(() =>
				this.prefService.getPrefs().pipe(
					switchMap((preferences) => {
						return of(UserPreferencesActions.prefs.getSuccess({ preferences }));
					}),
					catchError((error) =>
						of(UserPreferencesActions.prefs.getFail(new FailState(error)))
					)
				)
			)
		)
	);

	/**
	 * Fetch user preferences as soon as we know who the user is, without
	 * waiting for them to navigate to the settings page. Fires on fresh login
	 * and on session restore after a page reload. The underlying {@code get}
	 * effect hits the cache on repeat dispatches, so triggering this on every
	 * session refresh is cheap.
	 */
	loadOnAuth = createEffect(() =>
		this.actions.pipe(
			ofType(LoginProcessed, SessionRefreshed),
			map(() => UserPreferencesActions.prefs.get())
		)
	);

	constructor(
		private store: Store<AppState>,
		private actions: Actions,
		private prefService: UserPreferencesService
	) {}

	private canUseStoreValue(state: UserPreferencesState): boolean {
		return !_.has(state, "error") && !state.loading;
	}
}
